import { fetchAdminSenderProxyStats, fetchSenderProxyAdminConfig } from '@/api/admin';
import { PageScroll } from '@/layouts/page-layout';
import SenderProxyStatsCards from '@/pages/admin/settings/sender-proxy-stats-cards';
import ProxyImportsTable from '@/pages/admin/sender/proxies/proxy-imports-table';
import ProxyPoolSettingsForm from '@/pages/admin/sender/proxies/proxy-pool-settings-form';
import ProxyPoolTable from '@/pages/admin/sender/proxies/proxy-pool-table';
import ProxyUploadWizard from '@/pages/admin/sender/proxies/proxy-upload-wizard';
import type { AdminSenderProxyStatsResponse, SenderProxyFormat, SenderProxyProtocol } from '@/entities/sender.entity';
import { ShieldIcon } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

export default function AdminSenderProxiesPage() {
	const importsSectionRef = useRef<HTMLDivElement>(null);
	const [stats, setStats] = useState<AdminSenderProxyStatsResponse | null>(null);
	const [refreshKey, setRefreshKey] = useState(0);
	const [protocol, setProtocol] = useState<SenderProxyProtocol>('socks5');
	const [format, setFormat] = useState<SenderProxyFormat>('auto');

	const applyPageData = useCallback(
		(
			statsResult: PromiseSettledResult<AdminSenderProxyStatsResponse>,
			configResult: PromiseSettledResult<Awaited<ReturnType<typeof fetchSenderProxyAdminConfig>>>,
			refreshTables = false,
		) => {
			if (statsResult.status === 'fulfilled') {
				setStats(statsResult.value);
				if (refreshTables) {
					setRefreshKey(k => k + 1);
				}
			}

			if (configResult.status === 'fulfilled') {
				setProtocol(configResult.value.protocol);
				setFormat(configResult.value.format);
			}
		},
		[],
	);

	const loadPageData = useCallback(
		async (refreshTables = false) => {
			const results = await Promise.allSettled([
				fetchAdminSenderProxyStats(),
				fetchSenderProxyAdminConfig(),
			]);
			applyPageData(results[0], results[1], refreshTables);
		},
		[applyPageData],
	);

	const handleImported = useCallback(() => {
		void loadPageData(true);
		importsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
	}, [loadPageData]);

	useEffect(() => {
		let cancelled = false;
		void Promise.allSettled([fetchAdminSenderProxyStats(), fetchSenderProxyAdminConfig()]).then(results => {
			if (cancelled) {
				return;
			}
			applyPageData(results[0], results[1], false);
		});
		return () => {
			cancelled = true;
		};
	}, [applyPageData]);

	return (
		<PageScroll contentClassName='p-2 flex flex-col gap-4'>
			<div className='flex items-center gap-3'>
				<div className='rounded-lg bg-chart-2/15 p-2'>
					<ShieldIcon className='text-chart-2' size={20} />
				</div>
				<div>
					<h1 className='text-base font-semibold text-chart-2'>Прокси Sender</h1>
					<p className='text-xs text-muted-foreground'>Импорт, история и управление пулом Telethon</p>
				</div>
			</div>

			<SenderProxyStatsCards stats={stats} />

			<ProxyUploadWizard
				protocol={protocol}
				format={format}
				onProtocolChange={setProtocol}
				onFormatChange={setFormat}
				onImported={handleImported}
			/>

			<div ref={importsSectionRef}>
				<ProxyImportsTable refreshKey={refreshKey} />
			</div>

			<ProxyPoolTable refreshKey={refreshKey} />

			<ProxyPoolSettingsForm onSaved={() => void loadPageData(true)} />
		</PageScroll>
	);
}
